import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Avatar, Layout, message, Popconfirm } from 'antd';
import { UserAddOutlined, UserSwitchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../../../contexts/AuthContext';
import useLanguageContext from '../../../contexts/LanguageContext';
import Logo from '../../../assets/images/Logo-menu.png';
import { getLocalizationMenu, getProfileMenuItems } from './headerMenuUtils';
import HeaderUserAndLanguageDropdowns, {
  StyledDropdownContainer as BaseStyledDropdownContainer
} from './HeaderDropdowns';
import { useCreateSecondaryProfile } from '../../../utils/createSecondaryProfile';

const { Header } = Layout;

const StyledHeader = styled.div`
  padding: 0 40px;
  z-index: 8;
  position: fixed;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100vw;
  height: 72px;
`;

const StyledContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: end;
  width: fit-content;

  .profile-switch.ant-avatar {
    width: 40px;
    height: 40px;
    line-height: 40px;
    background: var(--ds-surface);
    color: var(--textColor);
    box-shadow: var(--ds-shadow-card);
    cursor: pointer;
  }
`;

const StyledDropdownContainer = styled(BaseStyledDropdownContainer)`
  :hover {
    background: transparent;
  }
`;

const LogoDiv = styled.div`
  height: 72px;
  line-height: 68px;
  transition: all 0.3s;
  z-index: 900;
  width: fit-content;

  img {
    display: inline-block;
    height: 56px;
    width: auto;
    vertical-align: middle;
  }
`;

const OwnerPensionerHeaderLayout = ({
  collapsed,
  collapseWidth,
  themeLogo
}) => {
  const navigate = useNavigate();
  const { user, setUser, profilesArr, dispatchAPI } = useAuthContext();
  const { _id: id } = user;
  const { dispatch: dispatchLocale } = useLanguageContext();
  const { t, i18n } = useTranslation();

  const [widthTrigger, setWidthTrigger] = useState('100vw');
  const role = `${user?.role?.split(':')[1].toLowerCase()}s`;
  const secondProfile = role === 'owners' ? 'pensioners' : 'owners';

  const { createSecondaryProfile } = useCreateSecondaryProfile(secondProfile);

  const onProfileMenuClick = ({ key }) => {
    if (key === 'logout') {
      dispatchAPI('LOGOUT');
      message.success(t('login.logoutSuccess'));
    }
  };

  const onLanguageMenuClick = ({ key }) => {
    dispatchLocale({ type: 'SET_LOCALE', locale: key });
  };

  const changeRole = async () => {
    if (user.role === 'users:OWNER') {
      const { data } = await dispatchAPI('PATCH', {
        url: `users/${id}/change-role`,
        body: { role: 'users:PENSIONER' }
      });
      navigate('/');
      setUser(data);
    } else {
      const { data } = await dispatchAPI('PATCH', {
        url: `users/${id}/change-role`,
        body: { role: 'users:OWNER' }
      });
      navigate('/');
      setUser(data);
    }
  };

  const localizationMenu = getLocalizationMenu(onLanguageMenuClick, i18n);
  const profileMenu = {
    onClick: onProfileMenuClick,
    items: getProfileMenuItems({
      user,
      t,
      navigate,
      profilePath: `/profile/${id}`
    })
  };

  useEffect(() => {
    if (collapsed) {
      if (collapseWidth === 0) {
        setWidthTrigger('100vw');
      } else {
        setWidthTrigger('calc(100vw - 80px)');
      }
    }
  }, [collapsed, collapseWidth]);

  return (
    <StyledHeader as={Header} width={widthTrigger}>
      <LogoDiv>
        <Link to="/">
          <img alt="Logo" src={Logo} />
        </Link>
      </LogoDiv>
      {themeLogo && (
        <img
          style={{
            width: 100,
            maxHeight: 50,
            position: 'absolute',
            top: 10,
            left: '50%',
            marginLeft: '-50px'
          }}
          src={themeLogo}
          alt="Company logo"
        />
      )}
      <StyledContainer>
        <StyledDropdownContainer>
          {profilesArr.length > 1 ? (
            <Avatar
              className="profile-switch"
              icon={<UserSwitchOutlined />}
              onClick={changeRole}
            />
          ) : (
            <Popconfirm
              title={t('settings.messages.add_profile', {
                customer:
                  secondProfile === 'owners' ? 'propriétaire' : 'retraité'
              })}
              cancelText={t('datatable.column.action.archive.cancel')}
              onConfirm={createSecondaryProfile}
              placement="topRight"
            >
              <Avatar className="profile-switch" icon={<UserAddOutlined />} />
            </Popconfirm>
          )}
        </StyledDropdownContainer>
        <HeaderUserAndLanguageDropdowns
          profileMenu={profileMenu}
          localizationMenu={localizationMenu}
          user={user}
          t={t}
          StyledDropdownContainerComponent={StyledDropdownContainer}
        />
      </StyledContainer>
    </StyledHeader>
  );
};

OwnerPensionerHeaderLayout.propTypes = {
  collapsed: PropTypes.bool.isRequired,
  collapseWidth: PropTypes.number.isRequired,
  themeLogo: PropTypes.string
};

OwnerPensionerHeaderLayout.defaultProps = {
  themeLogo: null
};

export default OwnerPensionerHeaderLayout;
